/**
 * El código de un QR impreso — `T-37`.
 *
 * Es lo único que viaja en `qr.fialrest.com/<codigo>` (`ADR-005`), así que es a
 * la vez la dirección y la credencial: quien tiene el código abre ese menú. De
 * ahí salen las tres reglas de abajo.
 *
 * ── 1 · ALEATORIO, nunca correlativo ───────────────────────────────────────
 * El plan de `F3` lo pone entre las tres decisiones que hunden esto si se
 * improvisan:
 *
 * > Con `a1, a2, a3…` cualquiera enumera la plataforma entera y puede ensuciar
 * > las métricas de tus clientes desde una terminal.
 *
 * Y no es sólo ensuciar `M-05`: un correlativo revela **cuántas cafeterías hay y
 * en qué orden entraron**, que es información de negocio de Lautinet, y permite
 * abrir el menú de cualquier cliente sin haber pisado su local.
 *
 * ── 2 · Se lee en voz alta, y se teclea mal ────────────────────────────────
 * Un QR se escanea, pero el código acaba dictándose por teléfono el día que la
 * cámara de alguien no enfoca. Por eso el alfabeto **no tiene `0`/`O`, `1`/`I`/`l`
 * ni `5`/`S`**: son los cuatro pares que se confunden leyendo y al teclear.
 *
 * Y por eso **todo en mayúsculas**: mezclar cajas duplica el alfabeto sin ganar
 * nada y obliga a preguntar «¿era la be grande o la chica?».
 *
 * ── 3 · Diez caracteres, y por qué ese número ──────────────────────────────
 * Con 29 símbolos, diez posiciones son 29¹⁰ ≈ **4.2 × 10¹⁴** combinaciones —
 * calculado, no estimado—. Para acertar uno de mil códigos vivos probando al
 * azar harían falta del orden de 10¹¹ intentos: no es un ataque, es un
 * imposible. Ocho ya bastarían hoy; diez dejan margen para que el piloto crezca
 * **sin cambiar los papeles ya pegados a las mesas**, que es lo único de todo
 * esto que no se puede rehacer.
 */
/**
 * El alfabeto, sin los pares que se confunden al dictar o al teclear.
 *
 * Fuera: `0`/`O`, `1`/`I`/`L`, `5`/`S`. Quedan **29** símbolos.
 */
export const ALFABETO_DEL_CODIGO = '2346789ABCDEFGHJKMNPQRTUVWXYZ';
/** Cuántos caracteres. Ver el punto 3 de la cabecera. */
export const LARGO_DEL_CODIGO = 10;
/**
 * ¿Tiene forma de código? **No dice si existe**, sólo si vale la pena buscarlo.
 *
 * Se usa antes de tocar la base, igual que `esSlugPosible` en el menú público:
 * lo que llega viene de la barra de direcciones de un desconocido, y una cadena
 * que no puede ser un código no merece un viaje a la base ni una fila de
 * registro.
 */
export const esCodigoPosible = (candidato) => FORMA.test(candidato);
/**
 * La forma, **derivada del alfabeto** y no escrita a mano.
 *
 * Con las clases escritas aparte —`[2-9A-HJ-NP-Z]`— habría dos sitios diciendo
 * qué símbolos valen, y el día que alguien quite uno del alfabeto esto seguiría
 * aceptándolo. El alfabeto es alfanumérico, así que no lleva ningún carácter con
 * significado dentro de una clase; hay una prueba que lo comprueba, porque el
 * día que alguien meta un `-` esta expresión pasaría a significar otra cosa sin
 * dar ningún error.
 */
const FORMA = new RegExp(`^[${ALFABETO_DEL_CODIGO}]{${LARGO_DEL_CODIGO}}$`);
/**
 * Un código nuevo.
 *
 * ── `crypto.getRandomValues` y no `Math.random` ────────────────────────────
 * `Math.random` es predecible: dos códigos seguidos dejan ver el estado del
 * generador, y de ahí salen los siguientes. Para un identificador que abre el
 * menú de un cliente eso no vale, y la diferencia de coste es ninguna.
 *
 * ── El sesgo del módulo, y por qué aquí no lo hay ──────────────────────────
 * Tomar `byte % 29` sobre 0..255 daría **más probabilidad a los 24 primeros
 * símbolos**: 256 no es múltiplo de 29. No es un fallo visible —los códigos
 * seguirían pareciendo aleatorios— y aun así reduce el espacio real.
 *
 * Se descartan los bytes que caen en la cola incompleta. El tope es el mayor
 * múltiplo de 29 que cabe en un byte, **232**; los 24 valores de 232 a 255 se
 * tiran y se pide otro. Se descarta el **9.4 %** de los bytes.
 */
export function generarCodigo(aleatorio, 
//  `F4` · Cuántos caracteres. Diez para el papel de la mesa, que es lo de
//  siempre; **seis** para el código efímero del comensal, que se dicta en un
//  mostrador con gente esperando y no tiene que durar más de tres minutos.
//
//  Es un parámetro y no una función aparte porque lo que hay que compartir es
//  justo lo delicado: el alfabeto sin pares confundibles y el descarte que
//  quita el sesgo del módulo. Una segunda copia de este bucle sería una
//  segunda oportunidad de escribir `%` sin el descarte y no notarlo nunca.
largo = LARGO_DEL_CODIGO) {
    const base = ALFABETO_DEL_CODIGO.length;
    const tope = Math.floor(256 / base) * base;
    let codigo = '';
    while (codigo.length < largo) {
        //  Se piden de sobra para no volver casi nunca: con un 9.4 % de descarte,
        //  pedir el doble de lo que falta acaba en una vuelta prácticamente siempre.
        for (const byte of aleatorio((largo - codigo.length) * 2)) {
            if (byte >= tope)
                continue;
            codigo += ALFABETO_DEL_CODIGO[byte % base];
            if (codigo.length === largo)
                break;
        }
    }
    return codigo;
}
//# sourceMappingURL=codigo-de-qr.js.map